import { Grid, Typography } from '@mui/material';
import { CollateralHistoryDetailRecord } from '@tassat/fred-definitions';
import { ButtonType, TransactionDetail, WalletAddress } from '@tassat/text';
import { formatEpochDate } from '@tassat/utility';
import React from 'react';

import { obscureAccountNo } from 'Lib/stringHelpers';
import { muiStyles } from 'SharedComponents/transactionDetails/transactionFirmDetails/styles';

import { PledgeDetailsProps } from '../types';

interface TransactionFromFirmDetail {
    currentUser: PledgeDetailsProps['currentUser'];
    selectedPledge: CollateralHistoryDetailRecord;
}

interface TransactionToFirmDetail {
    currentUser: PledgeDetailsProps['currentUser'];
    selectedPledgeParticipants: CollateralHistoryDetailRecord['PledgeParticipant'];
}

export const FromFirmDetail = (props: TransactionFromFirmDetail) => {
    const {
        currentUser,
        selectedPledge
    } = props;

    const classes = muiStyles(props);

    return (
        <Grid
            className={`${classes.detailSection} ${classes.detailSectionHighlighted}`}
            container
            direction='column'
            item
            spacing={0}
            wrap='nowrap'
        >
            <TransactionDetail
                id='transactionDetails_senderFirm'
                label='From Firm'
            />
            <Grid
                className={classes.firmDetails}
                container
                direction='row'
                justifyContent='space-between'
                spacing={0}
                wrap='nowrap'
            >
                <Typography
                    className={classes.firmAttribute}
                    id='transactionDetails_senderFirmName_value'
                    variant='body1'
                >
                    {selectedPledge?.PledgingFirmName}
                </Typography>
                <Typography
                    className={classes.firmAttribute}
                    id='transactionDetails_senderAccount_value'
                    variant='body1'
                >
                    {obscureAccountNo(selectedPledge?.PledgingAccount)}
                </Typography>
            </Grid>
            <TransactionDetail
                id='transactionDetails_senderWallet'
                label='Account Address'
            >
                <WalletAddress
                    buttonType={ButtonType.SMALL}
                    className={classes.attribute}
                    id='transactionDetails_senderWallet_value'
                    isAdmin={currentUser.IsAdmin}
                    walletAddress={selectedPledge.PledgingAccountAddress ?? ''}
                />
            </TransactionDetail>
            <TransactionDetail
                attribute={selectedPledge.PledgingAccountName}
                id='transactionDetails_senderAccountName'
                label='Account Name'
            />
            <TransactionDetail
                attribute={selectedPledge.PledgingUsername}
                id='transactionDetails_senderUsername'
                label='Username'
            />
            <TransactionDetail
                attribute={
                    selectedPledge.ApproverUsername !== '0' &&
                    selectedPledge.ApproverUsername ||
                    '-'
                }
                id='transactionDetails_approverUsername'
                label='Approver Username'
            />
            <TransactionDetail
                attribute={
                    selectedPledge.ApproverUsername &&
                    selectedPledge.ApproverUsername !== '0' &&
                    selectedPledge.ApproveTime &&
                    selectedPledge.ApproveTime !== '0' &&
                    formatEpochDate(selectedPledge.ApproveTime) ||
                    '-'
                }
                id='transactionDetails_approveTime'
                label='Approve Time'
            />
        </Grid>
    );
};

export const ToFirmDetail = (props: TransactionToFirmDetail) => {
    const {
        selectedPledgeParticipants,
        currentUser
    } = props;

    const classes = muiStyles(props);

    return (
        <Grid
            className={`${classes.detailSection} ${classes.detailSectionHighlighted}`}
            container
            direction='column'
            item
            spacing={0}
            wrap='nowrap'
        >
            <TransactionDetail
                id='transactionDetails_recipientFirm'
                label='To Firm'
            />
            {selectedPledgeParticipants && selectedPledgeParticipants.map((pledgeParticipant) => {
                return (
                    <React.Fragment key={pledgeParticipant.PledgeParticipantAccountAddress}>
                        <Grid
                            className={classes.firmDetails}
                            container
                            direction='row'
                            justifyContent='space-between'
                            spacing={0}
                            wrap='nowrap'
                        >
                            <Typography
                                className={classes.firmAttribute}
                                id='transactionDetails_recipientFirmName_value'
                                variant='body1'
                            >
                                {pledgeParticipant.PledgeParticipantFirmName}
                            </Typography>
                            <Typography
                                className={classes.firmAttribute}
                                id='transactionDetails_recipientAccount_value'
                                variant='body1'
                            >
                                {obscureAccountNo(pledgeParticipant.PledgeParticipantAccount)}
                            </Typography>
                        </Grid>
                        <TransactionDetail
                            id='transactionDetails_recipientAccount'
                            label='Account Address'
                        >
                            <WalletAddress
                                buttonType={ButtonType.SMALL}
                                className={classes.attribute}
                                id='transactionDetails_recipientAccount_value'
                                isAdmin={currentUser.IsAdmin}
                                walletAddress={pledgeParticipant.PledgeParticipantAccountAddress}
                            />
                        </TransactionDetail>
                        <TransactionDetail
                            attribute={pledgeParticipant.PledgeParticipantAccountName}
                            id='transactionDetails_recipientAccountName'
                            label='Account Name'
                        />
                    </React.Fragment>
                );
            })}
        </Grid>
    );
};